import { Fragment, useState } from "react";
import styled from "styled-components";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Slider } from "../../components/Slider";
import { StatusBadge } from "../../components/StatusBadge";
import { TableBody } from "../../components/Table/TableBody";
import { TableCell } from "../../components/Table/TableCell";
import { TableHead } from "../../components/Table/TableHead";
import { TableRow } from "../../components/Table/TableRow";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import {
  CheckContainer,
  CheckTableCell,
  CheckTableHeaderRow,
  CheckTitle,
} from "./CheckStyledComponents";
import { useGetImages } from "../../data/useGetImages";
import { ManualAcceptReject } from "./ManualAcceptReject";
import { IndividualChecksMap } from "./helper";
import { CheckResults } from "./CheckResults";
import { CheckFields } from "./CheckFields";
import { ApprovalComment } from "./approvalComment";
import { Button } from "../../components/Button";
import { DataTable } from "./ScreeningResultPrimitives";
import {
  isFailedCode,
  NfcCards,
  nfcSteps,
  ValidationDetail,
  validationSummary,
  ValidationDetails,
} from "./ElevatedIdResult";
import { DocumentImages } from "./DocumentImages";
import { BiometricsPanel } from "./BiometricsPanel";
import {
  checkStatusIntent,
  DetailList,
  Divider,
  Panel,
  PanelHeader,
  SubHeading,
} from "./IdLivenessPrimitives";

/** The row the provider's validation details break down (DOCUMENTVALIDATION). */
const isDocumentValidationRow = (check?: string) =>
  (check ?? "").trim().toLowerCase() === "document validation";

/**
 * The Document checks table. When the provider's validation details are
 * available they expand under the Document validation row, open by default
 * if any validation failed. With no such row they follow the table.
 */
const DocumentChecks = ({
  details,
  validations,
}: {
  details: { status: string; check?: string }[];
  validations: ValidationDetail[];
}) => {
  const hasFailures = validations.some((d) => isFailedCode(d.result));
  const [expanded, setExpanded] = useState(hasFailures);
  const hasValidationRow = details.some((d) =>
    isDocumentValidationRow(d.check)
  );

  return (
    <>
      {details.length > 0 && (
        <>
          <SubHeading>Document checks</SubHeading>
          <DataTable>
            <TableHead></TableHead>
            <TableBody>
              <CheckTableHeaderRow>
                <CheckTableCell>Check</CheckTableCell>
                <CheckTableCell>Result</CheckTableCell>
              </CheckTableHeaderRow>
              {details.map(({ status, check }, i) => {
                const expandable =
                  validations.length > 0 && isDocumentValidationRow(check);
                return (
                  <Fragment key={i}>
                    <TableRow>
                      <TableCell color="gray.80" borderBottom="none">
                        {check}
                      </TableCell>
                      <TableCell borderBottom="none">
                        <Flex alignItems="center" gap="12" flexWrap="wrap">
                          <StatusBadge intent={checkStatusIntent(status)}>
                            {status}
                          </StatusBadge>
                          {expandable && (
                            <Button
                              variant="blueText"
                              size="small"
                              px={0}
                              Icon={expanded ? FiChevronDown : FiChevronRight}
                              iconSize="16px"
                              iconColor="blue.primary"
                              onClick={() => setExpanded((prev) => !prev)}
                            >
                              {validationSummary(validations)}
                            </Button>
                          )}
                        </Flex>
                      </TableCell>
                    </TableRow>
                    {expandable && expanded && (
                      <TableRow>
                        <TableCell colSpan={2} borderBottom="none" pt={0}>
                          <ValidationPanel>
                            <ValidationDetails details={validations} />
                          </ValidationPanel>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </DataTable>
        </>
      )}

      {validations.length > 0 && !hasValidationRow && (
        <Box mt={details.length ? 3 : 0}>
          <Flex justifyContent="space-between" alignItems="center">
            <SubHeading>Document validation details</SubHeading>
            <Paragraph m="0px" fontSize={0} color="gray.50">
              {validationSummary(validations)}
            </Paragraph>
          </Flex>
          <ValidationDetails details={validations} />
        </Box>
      )}
    </>
  );
};

/**
 * The fixed document details in one column and the extracted fields across
 * the next two, with the same gap throughout so the three line up. Wraps to
 * one column above the other on a narrow panel.
 */
const DocumentInfo = styled.div`
  ${({ theme: { space } }) => `
    display: flex;
    flex-wrap: wrap;
    gap: ${space[3]} ${space[4]};

    & > * {
      flex: 1 1 220px;
      min-width: 0;
    }
    & > * + * {
      flex: 2 1 460px;
    }
  `}
`;

/** Inset under the Document validation row, so it reads as part of that row. */
const ValidationPanel = styled.div`
  ${({ theme: { colors, radii, space } }) => `
    background: ${colors.gray[5]};
    border-left: 3px solid ${colors.gray[20]};
    border-radius: ${radii[0]};
    padding: ${space[2]};
  `}
`;

export const IdentityLivenessCheck = (props) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);

  const instance = individualCheckData?.data?.instance;

  // Fetch document images (front/back)
  const { data: docImages } = useGetImages(
    instance?.images?.map((img) => img?.path || "")
  );

  const { data: livenessimages } = useGetImages(
    instance?.liveness?.images?.map((image) => (image?.path ? image?.path : ""))
  );

  const { data: face_match_urls } = useGetImages(
    instance?.face_match?.images?.reduce(
      (result, image) =>
        image.path ? result.push(image.path) && result : result,
      []
    )
  );

  // The provider's elevated result. Empty for checks completed before it was
  // stored, or when the provider's elevated service did not respond.
  const qualityChecks = instance?.elevated_result?.quality_check_details ?? [];
  const validations = instance?.elevated_result?.validation_details ?? [];
  const nfc = nfcSteps(instance?.elevated_result?.journey_steps);

  const checkStatus = individualCheckData?.data?.status?.status;

  const shouldShowReset =
    checkStatus === CheckStatusEnum.Review ||
    checkStatus === CheckStatusEnum.Accepted ||
    checkStatus === CheckStatusEnum.ManuallyAccepted ||
    checkStatus === CheckStatusEnum.ManuallyRejected;

  const shouldShowPoaManualActions = checkStatus === CheckStatusEnum.Review;

  const extractedFields =
    instance?.summary?.filter((summary) => summary?.value) ?? [];

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between" alignItems="center">
        <CheckTitle>
          {IndividualChecksMap["id_&_liveliness_check"].title}
        </CheckTitle>

        {shouldShowReset && (
          <ManualAcceptReject
            id={id}
            title={IndividualChecksMap["id_&_liveliness_check"].title}
            isMonitoring={false}
            showReset={shouldShowReset}
            showAccept={shouldShowPoaManualActions}
            showReject={shouldShowPoaManualActions}
            resetConfirmationMessage="Any existing ID & Liveliness information will be removed and the client will
          receive an email requesting they complete the check again."
          ></ManualAcceptReject>
        )}
      </Flex>

      {individualCheckData?.data?.status?.status && (
        <CheckResults
          status={individualCheckData?.data?.status?.status}
        ></CheckResults>
      )}

      {instance && instance?.approval_comment && (
        <ApprovalComment
          data={instance?.approval_comment}
          status={individualCheckData?.data?.status?.status}
        ></ApprovalComment>
      )}

      <CheckFields
        individual_checkfield_result={
          individualCheckData?.data?.individual_checkfield_result
        }
      ></CheckFields>

      {instance && (
        <Box px={4} pt={2}>
          {/* No status here: header.status is the overall ID & Liveness
              result, already shown at the top, not a check on this data. */}
          <Panel>
            <PanelHeader
              title={instance?.document_title || "Identity document"}
            />
            <DocumentInfo>
              <Box>
                <SubHeading>Document details</SubHeading>
                <DetailList
                  columns={extractedFields.length ? 1 : 2}
                  items={[
                    {
                      label: "Document type",
                      value: instance?.header?.document_type,
                    },
                    {
                      label: "Document name",
                      value: instance?.header?.document_name,
                    },
                    {
                      label: "Country",
                      value: instance?.header?.country_name,
                    },
                    {
                      label: "Initiated date",
                      value: instance?.header?.initiated_date,
                    },
                  ]}
                />

                {nfc.length > 0 && (
                  <Box mt={3}>
                    <SubHeading>NFC chip</SubHeading>
                    <NfcCards steps={nfc} />
                  </Box>
                )}
              </Box>

              {extractedFields.length > 0 && (
                <Box>
                  <SubHeading>Extracted from document</SubHeading>
                  <DetailList
                    columns={2}
                    items={extractedFields.map((summary) => ({
                      label: summary.property,
                      value: summary.value,
                    }))}
                  />
                </Box>
              )}
            </DocumentInfo>

            <DocumentImages
              images={instance?.images}
              base64Images={docImages}
              qualityChecks={qualityChecks}
            />
          </Panel>

          {instance?.personal_detail_match && (
            <Panel>
              <PanelHeader
                title={instance?.personal_detail_match?.title}
                status={instance?.personal_detail_match?.status}
              />
              {instance?.personal_detail_match?.result && (
                <Paragraph m="0px" mb={2} color="gray.60">
                  {instance?.personal_detail_match?.result}
                </Paragraph>
              )}
              <DataTable>
                <TableHead></TableHead>
                <TableBody>
                  <CheckTableHeaderRow>
                    <CheckTableCell></CheckTableCell>
                    <CheckTableCell>Personal details</CheckTableCell>
                    <CheckTableCell>ID document details</CheckTableCell>
                  </CheckTableHeaderRow>
                  {instance?.personal_detail_match?.details?.map(
                    ({ iDDocumentDetails, personalDetails, property }, i) => (
                      <TableRow key={i}>
                        <TableCell
                          fontWeight="600"
                          color="gray.80"
                          borderBottom="none"
                        >
                          {property}
                        </TableCell>
                        <TableCell color="gray.60" borderBottom="none">
                          {personalDetails ? personalDetails : "-"}
                        </TableCell>
                        <TableCell color="gray.60" borderBottom="none">
                          {iDDocumentDetails ? iDDocumentDetails : "-"}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </DataTable>
            </Panel>
          )}

          {instance?.verified_document && (
            <Panel>
              <PanelHeader
                title={instance?.verified_document?.title}
                status={instance?.verified_document?.status}
              />
              <DetailList
                items={[
                  {
                    label: "Recognised document",
                    value: instance?.verified_document?.recognised_document,
                  },
                  {
                    label: "Expected document",
                    value: instance?.verified_document?.expected_document,
                  },
                ]}
              />

              {(instance?.details?.length > 0 || validations.length > 0) && (
                <>
                  <Divider />
                  <DocumentChecks
                    details={instance?.details ?? []}
                    validations={validations}
                  />
                </>
              )}
            </Panel>
          )}

          {!instance?.verified_document && validations.length > 0 && (
            <Panel>
              <PanelHeader
                title="Document validation details"
                subtitle={validationSummary(validations)}
              />
              <ValidationDetails details={validations} />
            </Panel>
          )}

          <BiometricsPanel
            liveness={instance?.liveness}
            faceMatch={instance?.face_match}
            livenessImages={livenessimages}
            faceMatchImages={face_match_urls}
          />
        </Box>
      )}
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
